import { useState } from 'react'
import { faPaperclip } from '@fortawesome/pro-regular-svg-icons'
import { Icon, Link, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { API_BASE } from '../../lib/constants'
import { formatFileSize } from '../../lib/formatFileSize'
import type { MessageThreadDetail } from '../../lib/types'
import { formatShortDateTimeWithZone } from '@/utils/dateHelpers'

type ThreadComment = MessageThreadDetail['comments'][number]
type ThreadAttachment = NonNullable<ThreadComment['attachments']>[number]

const authorLabel = (author: ThreadComment['author']): string =>
  author === 'you' ? 'You' : 'Heard Support'

const dotColor: Record<ThreadComment['author'], string> = {
  you: 'bg-green-100',
  heard: 'bg-neutral-400',
}

interface ThreadAttachmentRowProps {
  attachment: ThreadAttachment
}

const rowClassName =
  'flex min-w-0 max-w-full items-center gap-1.5 rounded-md border border-neutral-200 px-2 py-1 text-xs'

const ThreadAttachmentRow = ({ attachment }: ThreadAttachmentRowProps) => {
  // A thumbnail the listing advertised can still be missing when the file is
  // fetched on its own, and a format the browser accepts can fail to decode,
  // so both steps down have to happen at runtime.
  const [thumbnailFailed, setThumbnailFailed] = useState(false)
  const [previewFailed, setPreviewFailed] = useState(false)

  const content = (
    <>
      <Icon
        icon={faPaperclip}
        className="shrink-0 text-[10px] text-neutral-400"
      />
      <span className="min-w-0 truncate">{attachment.fileName}</span>
      <span className="shrink-0 text-neutral-400">
        {formatFileSize(attachment.size)}
      </span>
    </>
  )

  if (!attachment.url) {
    return (
      <span className={mtc(rowClassName, 'text-neutral-400')}>
        {content}
        <span className="shrink-0">Not available</span>
      </span>
    )
  }

  // The server may serve this inline; a same-tab navigation would tear down the widget.
  const href = `${API_BASE}${attachment.url}`
  const showPreview =
    attachment.contentType.startsWith('image/') && !previewFailed
  const useThumbnail = Boolean(attachment.thumbnailUrl) && !thumbnailFailed

  if (showPreview) {
    return (
      <Link
        href={href}
        newPage
        variant="noUnderline"
        className={mtc(
          'block w-[150px] max-w-full overflow-hidden',
          'rounded-[8px] border border-moss-25 bg-neutral-0',
          'hover:border-neutral-300'
        )}
      >
        <img
          src={useThumbnail ? `${API_BASE}${attachment.thumbnailUrl}` : href}
          alt={attachment.fileName}
          loading="lazy"
          onError={() =>
            useThumbnail ? setThumbnailFailed(true) : setPreviewFailed(true)
          }
          className="block h-[84px] w-full object-cover"
        />
        <div className="flex min-w-0 flex-col gap-0.5 px-2 py-1.5">
          <Text
            variant={TypographyStyles.LABEL_SM}
            color={TextColors.HEADER}
            className="truncate"
            title={attachment.fileName}
          >
            {attachment.fileName}
          </Text>
          <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
            {formatFileSize(attachment.size)}
          </Text>
        </div>
      </Link>
    )
  }

  return (
    <Link
      href={href}
      newPage
      variant="noUnderline"
      className={mtc(
        rowClassName,
        'text-neutral-600 hover:border-neutral-300 hover:text-neutral-800'
      )}
    >
      {content}
    </Link>
  )
}

interface ThreadTimelineEntryProps {
  comment: ThreadComment
  isLast: boolean
}

const ThreadTimelineEntry = ({ comment, isLast }: ThreadTimelineEntryProps) => (
  <div
    className={mtc(
      'group/entry relative -ml-1 min-w-0 rounded-lg py-1.5 pl-6 transition-colors duration-150',
      isLast ? 'bg-green-0/60' : 'hover:bg-neutral-25'
    )}
  >
    <div
      className={mtc(
        'absolute left-1 top-3 h-[7px] w-[7px] rounded-full ring-2',
        isLast
          ? 'ring-green-0/60'
          : 'ring-neutral-0 group-hover/entry:ring-neutral-25',
        dotColor[comment.author]
      )}
    />

    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Text
          variant={TypographyStyles.LABEL_XS}
          className="font-semibold text-neutral-800"
        >
          {authorLabel(comment.author)}
        </Text>
        <span className="text-[11px] text-neutral-400">
          {formatShortDateTimeWithZone(comment.createdAt)}
        </span>
        {isLast && (
          <span className="text-[10px] font-semibold uppercase tracking-wider text-green-100">
            Latest
          </span>
        )}
      </div>
      {comment.htmlBody ? (
        // htmlBody is sanitised server-side with a fixed a/br/p allowlist; safe to inject directly.
        <div
          data-testid="thread-comment-body"
          className="body-sm mt-0.5 break-words [&_a]:break-words [&_a]:font-semibold [&_a]:text-green-100 [&_a]:underline [&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0"
          dangerouslySetInnerHTML={{ __html: comment.htmlBody }}
        />
      ) : (
        <Text
          variant={TypographyStyles.BODY_SM}
          testId="thread-comment-body"
          className="mt-0.5 whitespace-pre-wrap break-words"
        >
          {comment.body}
        </Text>
      )}
      {comment.attachments && comment.attachments.length > 0 && (
        <div className="mt-1.5 flex min-w-0 flex-col gap-1">
          {comment.attachments.map((attachment) => (
            <ThreadAttachmentRow key={attachment.id} attachment={attachment} />
          ))}
        </div>
      )}
    </div>
  </div>
)

interface ThreadTimelineProps {
  comments: ThreadComment[]
}

export const ThreadTimeline = ({ comments }: ThreadTimelineProps) => (
  <div className="relative ml-1.5 min-w-0">
    <div className="absolute bottom-2 left-[3px] top-2 w-px bg-neutral-200" />
    <div className="flex min-w-0 flex-col gap-1">
      {comments.map((comment, index) => (
        <ThreadTimelineEntry
          key={`${comment.createdAt}-${comment.author}-${index}`}
          comment={comment}
          isLast={index === comments.length - 1}
        />
      ))}
    </div>
  </div>
)
